import { useEffect, useId, useState, type FormEvent } from "react";
import { useCreateCorpusBinding, useUpdateCorpusBinding } from "../../api/queries";
import type { CorpusBinding } from "../../api/types";
import { Button } from "../../components/Button";
import { Modal } from "../../components/Modal";
import { useToast } from "../../toast/ToastContext";
import "../../components/Form.css";

interface BindCorpusModalProps {
  open: boolean;
  projectId: string;
  /** The project's current binding, or `undefined` to author a brand-new one. Driving
   * create-vs-edit off this (rather than a separate mode flag) keeps "what we're editing"
   * and "what mode we're in" from ever disagreeing, mirroring `EditUserModal`. */
  existing?: CorpusBinding;
  onClose: () => void;
}

const RUNG_OPTIONS: { value: string; label: string; hint: string }[] = [
  {
    value: "repo-watch",
    label: "Repo-watch",
    hint: "Zero-config default — byte-faithful and GitHub-App-attested, not CI-attested.",
  },
  {
    value: "ci-attested",
    label: "CI-attested",
    hint: "Strongest online rung — a comebye/corpus-sync GitHub Action ships an OIDC attestation.",
  },
  {
    value: "signed-commit",
    label: "Signed commit",
    hint: "Offline / air-gapped rung — a verified commit signature, no GitHub involved.",
  },
];

/**
 * Authors or wholesale-replaces a project's corpus binding (ADR-0053 §3) — the human step
 * that authorizes a Selkie handoff to populate this project's corpus. Session-only
 * server-side (S-9/S-11): reachable only because this UI itself IS a signed-in session, the
 * one thing a machine credential can never be here.
 *
 * This form covers the repo/ref/path/rung POLICY only — it does not walk an operator
 * through installing the GitHub App or setting up the CI action that would earn a
 * `ci-attested`/`signed-commit` handoff. That on-ramp handshake is real UX work of its own
 * (a GitHub App authorize redirect, a CI workflow to add) tracked as a follow-up; this form
 * is the "I already know my repo/path/rung" path an operator who has done that setup uses.
 */
export function BindCorpusModal({ open, projectId, existing, onClose }: BindCorpusModalProps) {
  const repoRefId = useId();
  const corpusPathId = useId();
  const rungId = useId();
  const issuerId = useId();
  const refsId = useId();
  const { pushToast } = useToast();

  const createMutation = useCreateCorpusBinding(projectId);
  const updateMutation = useUpdateCorpusBinding(projectId);
  const mutation = existing ? updateMutation : createMutation;

  const [repoRef, setRepoRef] = useState("");
  const [corpusPath, setCorpusPath] = useState(".comebye/corpus");
  const [expectedRung, setExpectedRung] = useState("repo-watch");
  const [expectedIssuer, setExpectedIssuer] = useState("");
  const [allowedRefsText, setAllowedRefsText] = useState("refs/heads/main");

  // Re-seed the form every time it opens, from whatever binding (if any) it opened with —
  // mirrors EditUserModal's re-seed-on-prop-change so editing a second project's binding
  // right after the first never shows stale fields.
  useEffect(() => {
    if (!open) return;
    setRepoRef(existing?.repoRef ?? "");
    setCorpusPath(existing?.corpusPath ?? ".comebye/corpus");
    setExpectedRung(existing?.expectedRung ?? "repo-watch");
    setExpectedIssuer(existing?.expectedIssuer ?? "");
    setAllowedRefsText(existing?.allowedRefs.join("\n") ?? "refs/heads/main");
  }, [open, existing]);

  function handleClose() {
    mutation.reset();
    onClose();
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const allowedRefs = allowedRefsText
      .split(/[\n,]/)
      .map((r) => r.trim())
      .filter(Boolean);
    mutation.mutate(
      {
        repoRef: repoRef.trim(),
        corpusPath: corpusPath.trim(),
        expectedRung,
        expectedIssuer: expectedIssuer.trim(),
        allowedRefs,
      },
      {
        onSuccess: () => {
          pushToast({
            title: existing ? "Corpus binding updated." : "Repository bound.",
            tone: "success",
          });
          handleClose();
        },
        // No toast on error — the inline form-error below stays legible while the
        // operator fixes a name collision or a missing field.
      },
    );
  }

  const refsPreview = allowedRefsText
    .split(/[\n,]/)
    .map((r) => r.trim())
    .filter(Boolean);
  const canSubmit = repoRef.trim().length > 0 && corpusPath.trim().length > 0 && refsPreview.length > 0 && !mutation.isPending;

  return (
    <Modal
      open={open}
      title={existing ? "Edit corpus binding" : "Bind a repository"}
      onClose={handleClose}
      initialFocusId={repoRefId}
    >
      <form onSubmit={handleSubmit} noValidate>
        {mutation.isError && (
          <p className="form-error" role="alert">
            {mutation.error instanceof Error ? mutation.error.message : "Couldn't save this binding."}
          </p>
        )}

        <label className="form-field" htmlFor={repoRefId}>
          <span>Repository</span>
          <input
            id={repoRefId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={repoRef}
            onChange={(e) => setRepoRef(e.target.value)}
            placeholder="acme/storefront"
            required
          />
          <span className="form-field__hint">
            The repository a handoff must prove it came from, exactly — e.g. "acme/storefront".
          </span>
        </label>

        <label className="form-field" htmlFor={corpusPathId}>
          <span>Corpus path</span>
          <input
            id={corpusPathId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={corpusPath}
            onChange={(e) => setCorpusPath(e.target.value)}
            required
          />
          <span className="form-field__hint">The corpus root within the repository.</span>
        </label>

        <label className="form-field" htmlFor={refsId}>
          <span>Allowed refs</span>
          <textarea
            id={refsId}
            value={allowedRefsText}
            onChange={(e) => setAllowedRefsText(e.target.value)}
            rows={3}
            required
          />
          <span className="form-field__hint">
            One git ref per line (e.g. "refs/heads/main") — a sync from any other ref is
            refused. At least one is required, or this binding would authorize nothing.
          </span>
        </label>

        <label className="form-field" htmlFor={rungId}>
          <span>Required provenance rung</span>
          <select id={rungId} value={expectedRung} onChange={(e) => setExpectedRung(e.target.value)}>
            {RUNG_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <span className="form-field__hint">{RUNG_OPTIONS.find((opt) => opt.value === expectedRung)?.hint}</span>
        </label>

        <label className="form-field" htmlFor={issuerId}>
          <span>Expected issuer (optional)</span>
          <input
            id={issuerId}
            type="text"
            autoComplete="off"
            spellCheck={false}
            value={expectedIssuer}
            onChange={(e) => setExpectedIssuer(e.target.value)}
            placeholder="https://token.actions.githubusercontent.com"
          />
          <span className="form-field__hint">
            Leave blank to accept any issuer the handoff signature already vouches for.
          </span>
        </label>

        <div className="form-actions">
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={!canSubmit}>
            {mutation.isPending ? "Saving…" : existing ? "Save changes" : "Bind repository"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
